import builder from './user-builder.png'

const content = `<p>The Builder pattern allows to separate code that needs an object from the code that creates this object. The code that needs an object describes what high-level properties it needs for the object, and the builder knows how to construct an object with these properties.</p>

<p>Let’s take an example to illustrate. A company provides an application containing free features and paid features. To access paid features the user must subscribe and have its identity verified by the company.</p>

<p>The code of the application contains a <code>User</code> class, that contains many things including:</p>

<ul><li>a boolean indicating if the identity of that user has been verified</li><li>a list of feature flags representing what features this user has access to</li></ul>

<p>Here is an excerpt of the <code>User</code> class:</p>

<pre><code class="language-ruby">class User
  attr_accessor :identity_verified

  def add_feature(feature)
    @features ||= &#91;]
    @features &lt;&lt; feature
  end

  def has_access_to_feature?(feature)
    @features.include? feature
  end
end</code></pre>

<p>Let’s say that users that use the free version of the application have access to feature 1 and feature 2, and paid users also have access to feature 3 instead, which is an improved version of features 1 and 2.</p>

<p>How to create paid users and free users in the code?</p>

<p>One way would be to have functions that create these types of users. For example for free users:</p>

<pre><code class="language-ruby">def create_free_user
  free_user = User.new
  free_user.identity_verified = false
  free_user.add_feature('feature1')
  free_user.add_feature('feature2')
  free_user
end</code></pre>

<p>And for paid users:</p>

<pre><code class="language-ruby">def create_paid_user
  paid_user = User.new
  paid_user.identity_verified = true
  paid_user.add_feature('feature3')
  paid_user
end</code></pre>

<p>This introduces a <a href="/levels-of-abstraction">level of abstraction</a> that decouples the code that needs free and paid users from how to construct those users. If the definition of free and paid user changes, for example if a free feature becomes paid, the code that uses paid users doesn’t have to change.</p>

<p>But this technique stops working when there are more parameters to construct users. For example, at some point the application revamped its look-and-feel, but gave the option for existing users (free and paid) to keep the old one for a while if they want. Those are called “legacy” users. Those users have the “legacy” feature flag.</p>

<p>To create a free legacy user with <code>create_free_user</code>, we’d have to introduce an <code>is_legacy</code> parameter:</p>

<pre><code class="language-ruby">def create_free_user(is_legacy: false)
  free_user = User.new
  free_user.identity_verified = false
  free_user.add_feature('feature1')
  free_user.add_feature('feature2')
  if (is_legacy)
    free_user.add_feature('legacy')
  end
end</code></pre>

<p>And we’d have to do the same with <code>create_paid_user</code>. And with every other parameter that impacts the construction of users. This doesn’t scale well.</p>

<h2 id="Introducing-a-Builder">Introducing a Builder</h2>

<p>A Builder is an object that offers various methods to construct another object. The fact that there are several methods is useful for a client to pick and choose which properties it needs for the object under construction.</p>

<p>In our example, the following builder class allows to create free, paid, legacy and not legacy users:</p>

<pre><code class="language-ruby">class UserBuilder
  def initialize
    @user = User.new
  end

  def free
    @user.identity_verified = false
    @user.add_feature('feature1')
    @user.add_feature('feature2')
    self
  end

  def paid
    @user = User.new
    @user.identity_verified = true
    @user.add_feature('feature3')
    self
  end

  def legacy
    @user.add_feature('legacy')
    self
  end

  def get
    @user
  end
end</code></pre>

<p>For example, to create free legacy users we can use the builder the following way:</p>

<pre><code class="language-ruby">user = UserBuilder.new.free.legacy.get</code></pre>

<p>The fact that each method returns the instance of the builder (with <code>self</code>) allows to chain the method calls on the builder.</p>

<p>Like with the previous methods like <code>create_free_user</code> the code that uses users doesn’t know about how to put together the specific properties of users, but the builder offers a composable way of assembling those properties.</p>

<p>The builder plays the role of an <a href="/indirection">Indirection</a> between the code that uses User and the User class itself during creation.</p>

<img src=${builder} alt="builder" />

<p>Some libraries like <a href="https://github.com/thoughtbot/factory_bot">FactoryBot</a> follow this pattern and allow to create builders with an elaborate syntax to define objects properties and build objects.</p>

<p>In more complex cases, there can be several builders that construct different types of objects. The various builders can offer the same interface with the properties names, allowing clients to use them with <a href="/polymorphism">Polymorphism</a>.</p>

<div class="notes">
<p>Key points</p>

<ul><li>The Builder decouples the code that needs an object from the code that creates the object</li><li>It exposes various properties for the object to create and hides how to build those properties into the object</li><li>The client of the builder can pick and choose and compose the various properties</li><li>Builder creates an <a href="/indirection">Indirection</a> between client code and object construction</li><li>Several builders can be used with Polymorphism if they offer the same interface for properties</li></ul>
</div>`

export default content
